// ============================================================================
// JSM CREATE SURVEY
// ============================================================================
import { fg } from '@atlaskit/platform-feature-flags';
import {
	JSM_CREATE_KB_ARTICLES_KEY,
	JSM_CREATE_KB_TOPICS_KEY,
	JSM_CREATE_EMBEDDED_REQUEST_KEY,
	JSM_CREATE_REQUEST_KEY,
	JSM_CREATE_REQUEST_V2_KEY,
	JSM_CREATE_SURVEY_KEY,
	JSM_QUEUE_CREATE_KEY,
	JSM_QUEUE_UPDATE_KEY,
	JSM_REQUEST_TYPE_PORTAL_GROUPS_UPDATE_KEY,
	JSM_REQUEST_TYPE_UPDATED_KEY,
	JSM_REQUEST_TYPE_WORKFLOW_UPDATE_KEY,
	JSM_SLA_CREATE_KEY,
	JSM_SLA_UPDATE_KEY,
	JSM_SUGGEST_AND_ADD_FIELDS_TO_REQUEST_TYPE_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { JSM_CREATE_KB_STAGING_AREA_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/jsm-create-kb-client';
import { JSM_QUEUE_SETTINGS_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/jsm-queue-settings-client';
import { JSM_REQUEST_TYPE_SETTINGS_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/jsm-request-type-settings-client';
import { JSM_SLA_SETTINGS_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/jsm-sla-settings-client';
import { JSM_CREATE_SURVEY_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/jsm-create-survey-client';

/**
 * Lookup map for fire-and-forget embed view actions.
 * Maps action key → { clientId, contentType }.
 *
 * To add a new embed view, just add an entry here — no extra if-branches needed.
 */
type JsmContentType =
	| 'jsm-create-survey'
	| 'jsm-queue-updated'
	| 'jsm-request-type-updated'
	| 'jsm-sla-updated';

const JSM_EMBED_VIEW_MAP: Record<string, { clientId: string; contentType: JsmContentType }> = {
	[JSM_QUEUE_CREATE_KEY]: {
		clientId: JSM_QUEUE_SETTINGS_CLIENT_ID,
		contentType: 'jsm-queue-updated',
	},
	[JSM_QUEUE_UPDATE_KEY]: {
		clientId: JSM_QUEUE_SETTINGS_CLIENT_ID,
		contentType: 'jsm-queue-updated',
	},
	[JSM_REQUEST_TYPE_UPDATED_KEY]: {
		clientId: JSM_REQUEST_TYPE_SETTINGS_CLIENT_ID,
		contentType: 'jsm-request-type-updated',
	},
	[JSM_REQUEST_TYPE_PORTAL_GROUPS_UPDATE_KEY]: {
		clientId: JSM_REQUEST_TYPE_SETTINGS_CLIENT_ID,
		contentType: 'jsm-request-type-updated',
	},
	[JSM_REQUEST_TYPE_WORKFLOW_UPDATE_KEY]: {
		clientId: JSM_REQUEST_TYPE_SETTINGS_CLIENT_ID,
		contentType: 'jsm-request-type-updated',
	},
	[JSM_SLA_CREATE_KEY]: {
		clientId: JSM_SLA_SETTINGS_CLIENT_ID,
		contentType: 'jsm-sla-updated',
	},
	[JSM_SLA_UPDATE_KEY]: {
		clientId: JSM_SLA_SETTINGS_CLIENT_ID,
		contentType: 'jsm-sla-updated',
	},
	[JSM_SUGGEST_AND_ADD_FIELDS_TO_REQUEST_TYPE_KEY]: {
		clientId: JSM_REQUEST_TYPE_SETTINGS_CLIENT_ID,
		contentType: 'jsm-request-type-updated',
	},
};

export const JSM_ACTION_KEYS: string[] = [
	JSM_CREATE_EMBEDDED_REQUEST_KEY,
	JSM_CREATE_REQUEST_KEY,
	JSM_CREATE_REQUEST_V2_KEY,
	JSM_CREATE_SURVEY_KEY,
	JSM_QUEUE_CREATE_KEY,
	JSM_QUEUE_UPDATE_KEY,
	JSM_REQUEST_TYPE_UPDATED_KEY,
	JSM_REQUEST_TYPE_PORTAL_GROUPS_UPDATE_KEY,
	JSM_REQUEST_TYPE_WORKFLOW_UPDATE_KEY,
	JSM_SLA_CREATE_KEY,
	JSM_SLA_UPDATE_KEY,
	JSM_SUGGEST_AND_ADD_FIELDS_TO_REQUEST_TYPE_KEY,
];

export const JSM_ACTION_KEYS_SET: Set<string> = new Set<string>(JSM_ACTION_KEYS);

export const JSM_CREATABLE_ACTION_KEYS_SET: Set<string> = new Set([
	JSM_CREATE_SURVEY_KEY,
	JSM_QUEUE_CREATE_KEY,
	JSM_QUEUE_UPDATE_KEY,
	JSM_REQUEST_TYPE_UPDATED_KEY,
	JSM_REQUEST_TYPE_PORTAL_GROUPS_UPDATE_KEY,
	JSM_REQUEST_TYPE_WORKFLOW_UPDATE_KEY,
	JSM_SUGGEST_AND_ADD_FIELDS_TO_REQUEST_TYPE_KEY,
]);

const isKbFromScratchActionKey = (actionKey: string): boolean =>
	(actionKey === JSM_CREATE_KB_TOPICS_KEY || actionKey === JSM_CREATE_KB_ARTICLES_KEY) &&
	fg('jsm_kb_from_scratch');

export const isJsmAction = (action: unknown): action is MessageAction => {
	return (
		typeof action === 'object' &&
		action !== null &&
		'key' in action &&
		(JSM_ACTION_KEYS_SET.has(action?.['key'] as string) ||
			isKbFromScratchActionKey(action?.['key'] as string))
	);
};

export const isJsmCreatableAction = (action: unknown): action is MessageAction =>
	isJsmAction(action) &&
	(JSM_CREATABLE_ACTION_KEYS_SET.has(action.key) || isKbFromScratchActionKey(action.key));

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const isJsmUpdatableAction = (action: { key: string }): boolean => false;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const isJsmStreamAction = (action: { key: string }): boolean => false;

export const generateJsmReceiverId = (action: unknown): string | undefined => {
	if (!isJsmAction(action)) {
		return undefined;
	}

	if (fg('solution_composer_master')) {
		// Embed view actions — driven by lookup map
		const embedView = JSM_EMBED_VIEW_MAP[action.key];
		if (embedView) {
			return embedView.clientId;
		}
	}

	if (isKbFromScratchActionKey(action.key)) {
		return JSM_CREATE_KB_STAGING_AREA_CLIENT_ID;
	}

	if (isJsmCreatableAction(action)) {
		return JSM_CREATE_SURVEY_CLIENT_ID;
	}

	return undefined;
};

export const getJsmContentType = (action: { key: string }): JsmContentType | undefined => {
	const actionKey = action.key;

	if (fg('solution_composer_master')) {
		// Embed view actions — driven by lookup map
		const embedView = JSM_EMBED_VIEW_MAP[actionKey];
		if (embedView) {
			return embedView.contentType;
		}
	}

	if (JSM_CREATABLE_ACTION_KEYS_SET.has(actionKey) || isKbFromScratchActionKey(actionKey)) {
		return 'jsm-create-survey';
	}
	return undefined;
};

// ============================================================================
// JSM PREVIEW CARDS
// ============================================================================
const PREVIEW_CARD_ACTION_KEYS = new Set([JSM_CREATE_SURVEY_KEY]);

export const isJsmPreviewCardAction = (action: { key: string }): boolean =>
	PREVIEW_CARD_ACTION_KEYS.has(action.key);
